import React, { useEffect, useMemo, useState } from 'react';

import { Button, Card, Chip, Stack, Typography } from '@mui/joy';

import { usePermissionsContext } from '../../../context/permissionsContext';
import { track } from '../../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../../services/analytics/events';
import { launcherEventProps, tourStepEventProps } from '../analytics';
import {
  ALWAYS_SHOW_FOR_TESTING,
  MOUNT_DELAY_MS,
  ONBOARDING_KEY,
  REMIND_LATER_BUTTON_TEXT,
  START_TOUR_BUTTON_TEXT,
  TOUR_ANALYTICS,
} from '../constants';
import { useHomeOnboardingSteps } from '../steps/useHomeOnboardingSteps';
import { launcherCardSx, launcherChipSx, launcherCtaButtonSx } from '../style';
import { useOnboardingTour } from '../tour';
import {
  useOnboardingDismissalStore,
  useShouldShowOnboarding,
} from '../useOnboardingDismissalStore';

const LAUNCHER_EVENT_PROPS = launcherEventProps(TOUR_ANALYTICS);

export const HomepageOnboardingLauncher: React.FC = () => {
  const steps = useHomeOnboardingSteps();
  const { userData } = usePermissionsContext();
  // Org Fastpass counts for the tutorial events (the dashboard tour highlights
  // "Use a Fastpass"). null when the plan is unlimited/unprovisioned, mirroring
  // YourPlanBenefits — keep the keys present so they're never absent in Segment.
  const tourEventProps = useMemo(
    () =>
      tourStepEventProps(TOUR_ANALYTICS, {
        fastpass_used: userData?.benefits?.fastpass?.used ?? null,
        fastpass_total: userData?.benefits?.fastpass?.limit ?? null,
      }),
    [userData?.benefits?.fastpass?.used, userData?.benefits?.fastpass?.limit]
  );
  const { canShow, start: startTour } = useOnboardingTour(steps, tourEventProps, {
    lockScroll: true,
  });
  const storageAllowsShowing = useShouldShowOnboarding(ONBOARDING_KEY);
  const shouldShowFromStorage = ALWAYS_SHOW_FOR_TESTING || storageAllowsShowing;
  const markShown = useOnboardingDismissalStore((s) => s.markShown);
  const recordRemindLater = useOnboardingDismissalStore((s) => s.recordRemindLater);
  const markCompleted = useOnboardingDismissalStore((s) => s.markCompleted);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!shouldShowFromStorage) return;
    const timer = window.setTimeout(() => {
      if (!ALWAYS_SHOW_FOR_TESTING) markShown(ONBOARDING_KEY);
      setVisible(true);
    }, MOUNT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShowFromStorage, markShown]);

  // PGV only when the launcher card is actually rendered to the user — gated on
  // the SAME condition as the render below, so we don't log a "screen shown" for
  // a card that returns null (e.g. visible but canShow false).
  useEffect(() => {
    if (canShow && shouldShowFromStorage && visible)
      track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, {
        ...LAUNCHER_EVENT_PROPS,
        ...tourEventProps,
      });
  }, [canShow, shouldShowFromStorage, visible, tourEventProps]);

  if (!canShow || !shouldShowFromStorage || !visible) return null;

  const start = () => {
    track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      ...tourEventProps,
      button_text: START_TOUR_BUTTON_TEXT,
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) markCompleted(ONBOARDING_KEY);
    startTour();
  };
  const remindLater = () => {
    track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      ...tourEventProps,
      button_text: REMIND_LATER_BUTTON_TEXT,
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) recordRemindLater(ONBOARDING_KEY);
  };

  return (
    <Card variant='outlined' sx={launcherCardSx}>
      <Chip color='primary' size='lg' sx={launcherChipSx}>
        New
      </Chip>
      <Stack>
        <Typography level='h3' color='primary' fontWeight={'xl'}>
          Your new homepage, personalized to your plan
        </Typography>

        <Typography level='body-md' sx={{ color: (theme) => theme.palette.text.secondary }}>
          We&rsquo;ve reorganized everything around your plan, so what matters to you is up front.
        </Typography>
      </Stack>

      <Stack direction='row' gap={1}>
        <Button variant='outlined' size='lg' onClick={remindLater}>
          {REMIND_LATER_BUTTON_TEXT}
        </Button>
        <Button variant='cta' sx={launcherCtaButtonSx} size='lg' onClick={start}>
          {START_TOUR_BUTTON_TEXT}
        </Button>
      </Stack>
    </Card>
  );
};
